// Shapes returned by /api/hr-dashboard (see api/src/utils/hr-metrics.ts and api/src/models/hr-dashboard.ts)

export type TurnoverFormula = "RAM_EXPOSURE" | "STANDARD_AVG";

export const TURNOVER_FORMULA_LABELS: Record<TurnoverFormula, string> = {
    RAM_EXPOSURE: "RAM formula: Left / (HC at start + Hired)",
    STANDARD_AVG: "Standard: Left / Average HC",
};

// One per dashboard tab; also the export ?view= value
export type HrView = "headcount" | "by-group" | "consultants" | "compare" | "movement" | "turnover" | "retention";
export type HrExportView = HrView;
export type HrGroupBy = "department" | "division";
export type HrExportFormat = "xlsx" | "csv";

export interface HrWarning {
    code: string;
    count: number;
    message: string;
}

interface HrMonthBase {
    key: string;
    label: string;
    quarter: number;
    isMtd: boolean;
    isFuture: boolean;
}

export interface HeadcountValues {
    hcBegin: number;
    hired: number;
    left: number;
    hcEnd: number;
    avgHc: number;
}

export interface HeadcountMonth extends HrMonthBase {
    values: HeadcountValues | null;
}

export interface HeadcountResult {
    fiscalYear: number;
    months: HeadcountMonth[];
    quarters: { quarter: number; values: HeadcountValues | null }[];
    ytd: HeadcountValues | null;
    warnings: HrWarning[];
}

export interface TurnoverValues {
    left: number;
    regrettable: number;
    nonRegrettable: number;
    unclassified: number;
    denominator: number;
    rate: number | null;
    regrettableRate: number | null;
    nonRegrettableRate: number | null;
    unclassifiedRate: number | null;
}

export interface TurnoverMonth extends HrMonthBase {
    values: TurnoverValues | null;
}

export interface TurnoverResult {
    fiscalYear: number;
    formula: TurnoverFormula;
    formulaDescription: string;
    quarterMax: number;
    months: TurnoverMonth[];
    quarters: { quarter: number; values: TurnoverValues | null; overThreshold: boolean | null }[];
    ytd: TurnoverValues | null;
    warnings: HrWarning[];
}

export interface HrDashboardConfig {
    turnoverFormula: TurnoverFormula;
    turnoverQuarterMax: number; // fraction: 0.033 = 3.3%
    smallGroupMin: number;
    updatedAt: string | null;
}

export interface HrTerminationReason {
    reason: string;
    count: number;
    regrettable: boolean | null;
}

export interface HrTermination {
    employeeId: string;
    employeeCode: string | null;
    name: string;
    department: string | null;
    hireDate: string | null;
    terminatedAt: string;
    reason: string | null;
    mappedRegrettable: boolean | null;
    overrideRegrettable: boolean | null;
    regrettable: boolean | null;
}

export interface CompareMonth extends HrMonthBase {
    employees: number | null;
    consultants: number | null;
    total: number | null;
}

export interface CompareResult {
    fiscalYear: number;
    months: CompareMonth[];
    warnings: HrWarning[];
}

export interface GroupValues extends HeadcountValues {
    transfers: number; // net transfers into the group
}

export interface GroupHeadcountMonth extends HrMonthBase {
    values: GroupValues | null;
}

// "subtotal" = a division row with its departments listed below it (department view only)
export type HrGroupKind = "subtotal" | "group";

// Where a department / division row sits in the hierarchy
export interface HrGroupPlace {
    name: string;
    division: string | null; // department view: the division the row belongs to
    kind: HrGroupKind;
}

export interface GroupHeadcount extends HrGroupPlace {
    hidden: boolean;
    months: GroupHeadcountMonth[];
    ytd: GroupValues | null;
}

export interface GroupHeadcountResult {
    fiscalYear: number;
    groupBy: HrGroupBy;
    smallGroupMin: number;
    historyStart: string | null;
    groups: GroupHeadcount[];
    total: HeadcountResult;
}

export interface RetentionRow extends HrGroupPlace {
    hidden: boolean;
    departed: number | null;
    avgTenureDepartedMonths: number | null;
    avgTenureActiveMonths: number | null;
    activeCount: number | null;
    turnover: number | null;
    companySplit: number | null;
}

export interface RetentionResult {
    fiscalYear: number;
    groupBy: HrGroupBy;
    formula: TurnoverFormula;
    formulaDescription: string;
    smallGroupMin: number;
    historyStart: string | null;
    rows: RetentionRow[];
    total: RetentionRow;
    warnings: HrWarning[];
}

// "2 years 5 months" or "—"
export const formatTenure = (months: number | null | undefined): string =>
    months === null || months === undefined ? "—" : `${Math.floor(months / 12)} years ${months % 12} months`;

// Footnote for the department / division views
export const historyNote = (historyStart: string | null, groupLabel: string): string =>
    historyStart
        ? `Each person is counted in the ${groupLabel} they were in on each date. Department history is recorded from ${historyStart.slice(0, 10)}; earlier dates use the first recorded ${groupLabel}.`
        : `Uses each person's current ${groupLabel} (no department history recorded yet).`;

// "27.6%" or "—"
export const formatPercent = (value: number | null | undefined, digits = 1): string =>
    value === null || value === undefined ? "—" : `${(value * 100).toFixed(digits)}%`;

export const formatAverage = (value: number): string =>
    Number.isInteger(value) ? String(value) : value.toFixed(1);
